<template>
  <spinner v-if="loading" />
  <div v-else>
    <become-member-alert
      v-if="showBecomeMemberAlert"
      :message="becomeMemberMessage"
      :organization-slug="organization.slug"
      button-color="#0054A5"
      @click="showBecomeMemberAlert = false"
    />
    <div v-if="!openCheckout" class="store-wrapper">
      <div class="store-container">
        <h1 id="title">{{ title }}</h1>
        <div class="store-items">
          <div class="left-container">
            <user-container
              :user="user"
              :organization-type="organization.type"
              @refetchUser="getUser"
            />
            <sport-organizations-store-item-container
              ref="storeItemContainer"
              :enable-store="!!user"
              :organization-slug="organization.slug"
              :initial-values="order"
              @updateOrder="updateOrder"
              @updateValidity="orderValid = $event"
            />
          </div>
          <aside>
            <div v-if="coverUrl" class="cover-container">
              <img :src="coverUrl" alt="cover" width="100%">
              <h1 id="title" class="aside-title">{{ title }}</h1>
            </div>
            <sport-organization-shopping-cart
              :order="order"
              :show-next="!!user && !!order.length && orderValid"
              :show-edit="false"
              style="margin-top: 1.5rem"
              @next="handleNextAction"
            />
          </aside>
        </div>
      </div>
      <simple-snackbar v-bind.sync="snackbar" />
    </div>
    <sport-organization-checkout-container
      v-else
      ref="checkoutContainer"
      :organization="organization"
      :user="user"
      :buyer="buyer"
      :started-at="startedAt"
      :order="order"
      :schema="schema"
      :title="title"
      @refetchUser="getUser"
      @edit="handleOpenCheckout"
      @submit="handleSubmit"
    />
  </div>
</template>

<script>
import { decodePaymentMethod } from '@/payment-methods'
import UserContainer from './user/UserContainer.vue'
import { CREATE_ORGANIZATION_STORE_ORDER } from '@/graphql/order.graphql'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import { GET_LOGGED_USER } from '@/graphql/user.graphql'
import moment from 'moment'
import { getOrganizationStoreOrderInfoByUUID, populateSportOrganizationStoreOrder } from '@/http'
import { createPurchaseCheckout, startPaymentCheckout } from '@/utils'
import SportOrganizationsStoreItemContainer from '@/components/accreditations/organization-store/store-items/SportOrganizationsStoreItemContainer.vue'
import SportOrganizationShoppingCart from '@/components/accreditations/organization-store/shopping-cart/SportOrganizationShoppingCart.vue'
import SportOrganizationCheckoutContainer from '@/components/accreditations/organization-store/store-checkout/SportOrganizationCheckoutContainer.vue'
import BecomeMemberAlert from '@/components/tickets/become-member-alert.vue'
import i18next from 'i18next'

export default {
  name: 'sport-store-container',
  components: {
    BecomeMemberAlert,
    SportOrganizationCheckoutContainer,
    SportOrganizationShoppingCart,
    SportOrganizationsStoreItemContainer,
    SimpleSnackbar,
    UserContainer,
  },
  props: {
    organization: Object,
    schema: {
      type: Object,
      default: () => {},
    },
    title: String,
    coverUrl: String,
  },
  data() {
    return {
      openCheckout: false,
      order: [],
      orderValid: true,
      orderUUID: null,
      snackbar: {},
      user: null,
      buyer: {
        firstName: '',
        lastName: '',
        email: '',
        address: '',
      },
      startedAt: null,
      loading: true,
      showBecomeMemberAlert: false,
      becomeMemberMessage: '',
    }
  },
  async mounted() {
    await this.getUser()
    await this.parseSavedOrder(this.organization.id)

    this.loading = false
  },
  methods: {
    updateOrder(items) {
      return (this.order = items)
    },
    handleOpenCheckout() {
      this.openCheckout = !this.openCheckout
    },
    async handleNextAction(price) {
      const orderItemIds = this.order.map(oi => oi.id)

      const { errors, data } = await this.$apollo.mutate({
        mutation: CREATE_ORGANIZATION_STORE_ORDER,
        variables: { order: { orderItemIds, price } },
      })
      if (errors) {
        const errorMessage = errors[0].message
        if (errorMessage === 'User does not have a membership!') {
          i18next.changeLanguage('hr')

          this.becomeMemberMessage = errorMessage
          this.showBecomeMemberAlert = true
          this.$refs.storeItemContainer.disableSportMembershipTickets(this.order)
          this.order = this.order.filter(oi => !oi.itemMeta.sportMembershipTicket)

          return
        }

        return this.handleOpenSnackbar(errorMessage, 'error')
      }
      const { createOrganizationStoreOrder } = data
      localStorage.setItem(
        this.buildLocalStoreItemName(),
        JSON.stringify({
          UUID: createOrganizationStoreOrder.uuid,
          startedAt: new Date(),
        }),
      )
      this.startedAt = new Date().toISOString()
      this.orderUUID = createOrganizationStoreOrder.uuid
      this.handleOpenCheckout()
    },
    async getUser() {
      const { data } = await this.$apollo.query({
        query: GET_LOGGED_USER,
      })
      if (data.me) {
        this.user = data.me
        this.buyer = {
          paymentMethod: (this.buyer && this.buyer.paymentMethod) || null,
          firstName: this.user.firstName,
          lastName: this.user.lastName,
          email: this.user.email,
        }
      } else {
        this.buyer = {
          paymentMethod: (this.buyer && this.buyer.paymentMethod) || null,
        }
      }
    },
    buildLocalStoreItemName() {
      const type =
        this.$route.name === this.routes.shared.sportSubscriptionsStore
          ? 'Subscription'
          : this.$route.name === this.routes.shared.sportMembershipsStore
            ? 'Membership'
            : ''

      return `organization${type}StoreOrder`
    },

    async parseSavedOrder(organizationId) {
      const savedOrder = localStorage.getItem(this.buildLocalStoreItemName())

      if (!savedOrder) {
        return null
      }
      const order = JSON.parse(savedOrder)
      const { UUID, startedAt } = order
      const differenceInMinutes = moment(new Date()).diff(moment(startedAt), 'minutes')
      if (differenceInMinutes >= 15) {
        this.handleClearLocalStorage()

        return null
      }
      this.startedAt = startedAt
      this.orderUUID = UUID
      const found = await getOrganizationStoreOrderInfoByUUID(UUID)
      if (!found) {
        this.handleClearLocalStorage()

        return
      }
      if (found.purchases[0].orderItem.organizationId !== organizationId.toString()) {
        return null
      }
      this.updateOrder(found.purchases.map(p => p.orderItem))

      return this.handleOpenCheckout()
    },
    handleOpenSnackbar(msg, color) {
      return (this.snackbar = {
        open: true,
        messageProp: msg,
        color,
      })
    },
    handleClearLocalStorage() {
      localStorage.removeItem(this.buildLocalStoreItemName())
    },
    async handleSubmit() {
      if (!this.buyer.paymentMethod && this.$refs.checkoutContainer.$refs.shoppingCart.getTotalPrice() > 0) return
      const { acceptTermsAndConditions, ...rest } = this.buyer

      const { data, errors } = await populateSportOrganizationStoreOrder({
        orderUUID: this.orderUUID,
        buyer: { ...rest, paymentMethod: decodePaymentMethod(rest.paymentMethod || 'CreditCard').paymentMethod },
      })
      if (errors) {
        return this.handleOpenSnackbar(errors[0].message, 'error')
      }
      this.handleClearLocalStorage()
      const price = this.$refs.checkoutContainer.$refs.shoppingCart.getTotalPrice()
      if (!price) {
        await this.goTo(this.routes.sportOrganizer.storeSuccessfulPaymentSport, {
          orderUUID: this.orderUUID,
        })

        return
      }
      const { populateSportOrganizationStoreOrder: orderInfo } = data

      const tickets = this.order
      const buyerInfo = {
        attendee: { ...rest, street: rest.address },
        paymentMethod: this.buyer.paymentMethod,
      }
      const checkout = await createPurchaseCheckout(
        buyerInfo,
        `EUR ${price}`,
        orderInfo,
        i18next.language,
        tickets,
        '',
        { redirectType: 'store' },
      )
      await startPaymentCheckout(checkout)
    },
  },
}
</script>

<style scoped>
.store-wrapper {
  display: flex;
  justify-content: center;
  padding: 2rem 7rem;
}
.store-items {
  display: flex;
  gap: 1.5rem;
}
.store-container {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}
#title {
  color: #333;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 36px;
  font-style: normal;
  font-weight: 800;
  line-height: 120%; /* 43.2px */
  letter-spacing: -1.08px;
}
.left-container {
  flex: 2;
  width: 800px;
}
aside {
  flex: 1;
  width: 400px;
}
@media screen and (max-width: 1260px) {
  .store-items {
    flex-direction: column;
  }
  .left-container {
    width: 100%;
  }
  aside {
    width: 100%;
  }
  .store-wrapper {
    padding: 0 2rem;
  }
}
@media screen and (max-width: 800px) {
  .store-wrapper {
    padding: 0;
  }
}
.cover-container {
  border-radius: 0.25rem;
  border: 1px solid #0054a5;
}
.aside-title {
  padding: 1rem 1.5rem;
}
</style>
